import { z } from 'zod';

import { MAX_REASON_LENGTH, THUMBNAIL_MIME_TYPES } from './product-registration';

// The catalog-photo backfill's wire contracts with SBC (ENG-2382, ENG-2482). SBC's cron sends a
// batch to `POST /api/catalog-photos/render`; the platform then reads each source and records
// each render at SBC's `POST /api/agent-platform/catalog-photos/<name>`, as a candidate that an
// admin approves before `item_photos.url` changes.
//
// Every answer is parsed, unlike the product-registration reads: none of it reaches a model's
// prompt unread, the platform acts on each field, and a drift must fail loudly.

export const CATALOG_PHOTO_READ_MAX_IDS = 10;

// One render call's cap: SBC reads at most this many sources at once. The call's time budget,
// not this cap, is what keeps it inside Cloud Run's request timeout when photos need the model.
export const CATALOG_PHOTO_RENDER_MAX_IDS = CATALOG_PHOTO_READ_MAX_IDS;

export const CATALOG_PHOTO_ROUTES = ['alpha-cutout', 'white-backdrop', 'needs-cutout'] as const;
export const CatalogPhotoRouteSchema = z.enum(CATALOG_PHOTO_ROUTES);
export type CatalogPhotoRoute = z.infer<typeof CatalogPhotoRouteSchema>;

export const CatalogPhotoReadRequestSchema = z.object({
  photo_ids: z.array(z.uuid()).min(1).max(CATALOG_PHOTO_READ_MAX_IDS),
});
export type CatalogPhotoReadRequest = z.infer<typeof CatalogPhotoReadRequestSchema>;

// Unknown ids are omitted. `source_url` is always the brand's original, even for a photo whose
// white version is already live, so a re-render never composes a render of a render.
export const CatalogPhotoReadResponseSchema = z.object({
  photos: z.array(z.object({ photo_id: z.uuid(), item_id: z.uuid(), source_url: z.string() })),
});
export type CatalogPhotoReadResponse = z.infer<typeof CatalogPhotoReadResponseSchema>;

const CandidateImageSchema = z.object({
  mime_type: z.enum(THUMBNAIL_MIME_TYPES),
  // Base64, capped at 3 MB decoded by SBC, which sniffs the real type and answers 400
  // `bad_image` on a disagreement.
  data: z.string().min(1),
});

const RenderedOutcomeSchema = z.object({
  kind: z.literal('rendered'),
  image: CandidateImageSchema,
  route: CatalogPhotoRouteSchema,
  used_model: z.boolean(),
  self_check: z.object({ match: z.boolean(), reason: z.string().max(MAX_REASON_LENGTH) }),
});

const NeedsHumanOutcomeSchema = z.object({
  kind: z.literal('needs_human'),
  // Null only when the source could not be decoded at all, so no route was ever read.
  route: CatalogPhotoRouteSchema.nullable(),
  reason: z.string().max(MAX_REASON_LENGTH),
  // True only for a vision-check failure: SBC then re-queues the photo for one more render.
  retryable: z.boolean().default(false),
  // The render the check rejected, present only when `retryable` is: SBC stores it so a person
  // can see it and approve it anyway, and it never goes live without that choice.
  image: CandidateImageSchema.optional(),
});

// The source is already on white (95% of its border at 245 or more) and passed the self-audit,
// so nothing is rendered and nothing is stored but the reason.
const AlreadyWhiteOutcomeSchema = z.object({
  kind: z.literal('already_white'),
  reason: z.string().max(MAX_REASON_LENGTH),
});

// `source_url` is the one the platform fetched; SBC answers 409 `stale_source` when the photo's
// source has changed since, so a render of a replaced image is never stored against it.
export const CatalogPhotoRecordCandidateRequestSchema = z.object({
  photo_id: z.uuid(),
  source_url: z.string(),
  outcome: z.discriminatedUnion('kind', [
    RenderedOutcomeSchema,
    NeedsHumanOutcomeSchema,
    AlreadyWhiteOutcomeSchema,
  ]),
});
export type CatalogPhotoRecordCandidateRequest = z.infer<
  typeof CatalogPhotoRecordCandidateRequestSchema
>;

export const CatalogPhotoRecordCandidateResponseSchema = z.object({
  candidate_id: z.uuid(),
  status: z.enum(['pending', 'needs_human', 'already_white']),
  candidate_url: z.string().nullable(),
});
export type CatalogPhotoRecordCandidateResponse = z.infer<
  typeof CatalogPhotoRecordCandidateResponseSchema
>;

export const CatalogPhotoRenderRequestSchema = z.object({
  photo_ids: z.array(z.uuid()).min(1).max(CATALOG_PHOTO_RENDER_MAX_IDS),
});
export type CatalogPhotoRenderRequest = z.infer<typeof CatalogPhotoRenderRequestSchema>;

// What a render answers per photo. `failed` means nothing was recorded, so the photo stays in
// the queue; `not_started` means the call ran out of time before reaching it.
export const CatalogPhotoRenderStatusSchema = z.enum([
  'pending',
  'needs_human',
  'already_white',
  'failed',
  'not_started',
]);
export type CatalogPhotoRenderStatus = z.infer<typeof CatalogPhotoRenderStatusSchema>;

export const CatalogPhotoRenderResultSchema = z.object({
  photo_id: z.string(),
  status: CatalogPhotoRenderStatusSchema,
  candidate_url: z.string().nullable().optional(),
  used_model: z.boolean().optional(),
  route: CatalogPhotoRouteSchema.nullable().optional(),
  reason: z.string().optional(),
  // On `needs_human` only: true when the render failed its check, so the model was spent.
  retryable: z.boolean().optional(),
});
export type CatalogPhotoRenderResult = z.infer<typeof CatalogPhotoRenderResultSchema>;

export const CatalogPhotoRenderResponseSchema = z.object({
  photos: z.array(CatalogPhotoRenderResultSchema),
});
export type CatalogPhotoRenderResponse = z.infer<typeof CatalogPhotoRenderResponseSchema>;
